import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import type { RequestedCommercialTerms } from '@solar/contracts';
import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { ApprovedRepaymentSchedule } from '../../../components/ApprovedRepaymentSchedule';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useAuth } from '../../../features/auth/auth-context';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { customersApi } from '../../../features/customers/customers.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { calculateCommercialSummary } from '../../../lib/commercial-summary';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatBusinessDate } from '../../../lib/format-date';
import { routeTo } from '../../../routes/paths';
import styles from '../AppPages.module.css';

const FINAL_TERM_STATUSES = [
  'APPROVED',
  'AWAITING_CUSTOMER_ACCEPTANCE',
  'ACCEPTED',
  'AWAITING_MANDATE',
  'MANDATE_ACTIVE',
  'ACTIVE',
  'SUSPENDED',
  'COMPLETED',
  'CANCELLED',
  'DEFAULTED',
];

export function ApplicationDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [confirmedOfferVersion, setConfirmedOfferVersion] = useState<number | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signatureDocumentId, setSignatureDocumentId] = useState<string | null>(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);

  const application = useQuery({
    queryKey: ['applications', id],
    queryFn: () => applicationsApi.get(id),
  });
  const draftProduct = useQuery({
    queryKey: ['catalogue', 'products', 'customer', application.data?.productId],
    queryFn: () => catalogueApi.getCustomerProduct(application.data?.productId ?? ''),
    enabled: application.data?.status === 'DRAFT',
    retry: false,
  });
  const contractPreview = useQuery({
    queryKey: ['applications', id, 'contract-preview', application.data?.version],
    queryFn: () => applicationsApi.getContractPreview(id),
    enabled:
      application.data?.status === 'AWAITING_CUSTOMER_ACCEPTANCE' &&
      application.data?.offerExpired !== true,
  });

  useEffect(() => {
    if (signatureFile === null) return;
    const url = URL.createObjectURL(signatureFile);
    setSignaturePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [signatureFile]);
  const draftPlan = useQuery({
    queryKey: ['catalogue', 'plans', 'customer', application.data?.planTemplateId],
    queryFn: () => catalogueApi.getCustomerRepaymentPlan(application.data?.planTemplateId ?? ''),
    enabled: application.data?.status === 'DRAFT',
    retry: false,
  });
  const draftEstimate = useMemo(() => {
    if (draftProduct.data === undefined || draftPlan.data === undefined) return null;

    try {
      return calculateCommercialSummary({
        productPrice: draftProduct.data.basePrice,
        discountAmount: '0.00',
        depositType: draftPlan.data.depositType,
        depositValue: draftPlan.data.depositValue,
        tenureMonths: draftPlan.data.tenureMonths,
      });
    } catch {
      return null;
    }
  }, [draftPlan.data, draftProduct.data]);

  const submit = useMutation({
    mutationFn: () => applicationsApi.submit(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const accept = useMutation({
    mutationFn: () => {
      if (signatureDocumentId === null) throw new Error('Upload your signature before accepting.');
      if (
        application.data === undefined ||
        contractPreview.data?.offerVersion !== application.data.version ||
        confirmedOfferVersion !== application.data.version
      )
        throw new Error('Review and confirm the current final offer before accepting.');
      return applicationsApi.accept(id, signatureDocumentId, application.data.version);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const uploadSignature = useMutation({
    mutationFn: async () => {
      if (signatureFile === null) throw new Error('Select a signature image.');
      return customersApi.uploadSignature(signatureFile);
    },
    onSuccess: (document) => {
      setSignatureDocumentId(document.id);
      setSignatureError(null);
    },
  });

  if (application.isPending) {
    return <PageSpinner label="Loading application" />;
  }

  if (application.isError) {
    return <Alert tone="error">{toDisplayMessage(application.error)}</Alert>;
  }

  const data = application.data;
  const canSubmit =
    can(PERMISSIONS.SUBSCRIPTIONS_UPDATE) &&
    (data.status === 'DRAFT' || data.status === 'CHANGES_REQUESTED');
  const canAccept = data.status === 'AWAITING_CUSTOMER_ACCEPTANCE' && data.offerExpired !== true;
  const requestedTerms = data.requestedTerms ?? null;
  const draftSaved = (location.state as { draftSaved?: boolean } | null)?.draftSaved === true;
  const showsFinalTerms = FINAL_TERM_STATUSES.includes(data.status) && data.productPrice !== null;
  const isPreApproval = ['SUBMITTED', 'UNDER_REVIEW', 'CHANGES_REQUESTED', 'REJECTED'].includes(
    data.status,
  );

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>
          {data.productNameSnapshot ?? requestedTerms?.productName ?? 'Application'}
        </h1>
        <StatusBadge status={data.status} />
      </header>

      {(submit.isError || accept.isError || uploadSignature.isError) && (
        <Alert tone="error">
          {toDisplayMessage(submit.error ?? accept.error ?? uploadSignature.error)}
        </Alert>
      )}
      {submit.isSuccess && (
        <Alert tone="success">
          <strong>Application submitted successfully.</strong>
          <br />
          We&apos;ll review your application and confirm the final product price and repayment terms
          before asking you to accept an offer. You can&apos;t edit the application while it is
          under review. No repayment or Direct Debit mandate has been created yet.
          {submit.data.submittedAt !== null && (
            <>
              <br />
              Submitted {new Date(submit.data.submittedAt).toLocaleString()} · Status: Submitted ·
              Next step: application review
            </>
          )}
        </Alert>
      )}
      {accept.isSuccess && (
        <Alert tone="success">
          Final terms accepted at{' '}
          {accept.data.acceptedAt === null
            ? 'the recorded acceptance time'
            : new Date(accept.data.acceptedAt).toLocaleString()}
          . <Link to={routeTo.customerContract(id)}>View your preserved contract copy</Link>. Direct
          Debit setup is the next step.
        </Alert>
      )}
      {draftSaved && (
        <Alert tone="success">
          Your draft application has been saved. Review it below, then submit it when you are ready.
        </Alert>
      )}
      {data.status === 'SUBMITTED' && !submit.isSuccess && (
        <Alert tone="info">
          Your application was submitted
          {data.submittedAt === null ? '' : ` on ${new Date(data.submittedAt).toLocaleString()}`}.
          Final terms will be confirmed after review. No repayment or Direct Debit mandate has been
          created yet.
        </Alert>
      )}
      {data.status === 'UNDER_REVIEW' && (
        <Alert tone="info">
          Your application is being reviewed. Final terms will be confirmed before you are asked to
          accept an offer.
        </Alert>
      )}

      {data.status === 'DRAFT' && (
        <section className={styles.card} aria-labelledby="selected-terms-heading">
          <h2 className={styles.cardTitle} id="selected-terms-heading">
            Selected / estimated terms
          </h2>
          {draftEstimate === null ? (
            <Alert tone="info">
              Current estimates are unavailable. Your saved application details are shown below.
            </Alert>
          ) : (
            <dl className={styles.definitionList}>
              <dt>Product</dt>
              <dd>{draftProduct.data?.name}</dd>
              <dt>Repayment plan</dt>
              <dd>{draftPlan.data?.name}</dd>
              <dt>Estimated product price</dt>
              <dd>{formatNgn(draftEstimate.productPrice)}</dd>
              <dt>Estimated deposit</dt>
              <dd>{formatNgn(draftEstimate.depositAmount)}</dd>
              <dt>Estimated financed amount</dt>
              <dd>{formatNgn(draftEstimate.financedAmount)}</dd>
              <dt>Estimated instalment</dt>
              <dd>{formatNgn(draftEstimate.instalmentAmount)}</dd>
              <dt>Tenure</dt>
              <dd>{draftEstimate.tenureMonths} months</dd>
            </dl>
          )}
          <PaymentPreferences
            adminDateFlow={data.repaymentScheduleModel === 'ADMIN_APPROVED_DATE_V1'}
            debitDay={data.preferredDebitDay}
            firstDueDate={data.plannedFirstDueDate}
          />
          <p>These are estimates. Final terms will be confirmed after review.</p>
        </section>
      )}

      {requestedTerms !== null && data.status !== 'DRAFT' && (
        <RequestedTerms terms={requestedTerms} />
      )}

      {isPreApproval && requestedTerms === null && (
        <section className={styles.card} aria-labelledby="legacy-requested-terms-heading">
          <h2 className={styles.cardTitle} id="legacy-requested-terms-heading">
            Requested terms
          </h2>
          <Alert tone="info">
            This application predates requested-term snapshots. Final commercial terms will be
            confirmed after review.
          </Alert>
          <PaymentPreferences
            adminDateFlow={data.repaymentScheduleModel === 'ADMIN_APPROVED_DATE_V1'}
            debitDay={data.preferredDebitDay}
            firstDueDate={data.plannedFirstDueDate}
          />
        </section>
      )}

      {showsFinalTerms && (
        <section className={styles.card} aria-labelledby="final-terms-heading">
          <h2 className={styles.cardTitle} id="final-terms-heading">
            Approved / final terms
          </h2>
          {data.status === 'AWAITING_CUSTOMER_ACCEPTANCE' && (
            <p>Review these final terms before accepting the offer.</p>
          )}
          <dl className={styles.definitionList}>
            <dt>Product</dt>
            <dd>{data.productNameSnapshot ?? requestedTerms?.productName ?? '—'}</dd>
            <dt>Product price</dt>
            <dd>{formatNgn(data.productPrice)}</dd>
            <dt>Discount</dt>
            <dd>{formatNgn(data.discountAmount)}</dd>
            <dt>Deposit</dt>
            <dd>{formatNgn(data.depositAmount)}</dd>
            <dt>Financed amount</dt>
            <dd>{formatNgn(data.financedAmount)}</dd>
            <dt>Regular instalment</dt>
            <dd>{formatNgn(data.instalmentAmount)}</dd>
            <dt>Final instalment</dt>
            <dd>{formatNgn(data.finalInstalmentAmount)}</dd>
            <dt>Total repayable</dt>
            <dd>{formatNgn(data.totalRepayableAmount)}</dd>
            <dt>Tenure</dt>
            <dd>{data.tenureMonths ?? '—'} months</dd>
            <dt>Repayment plan</dt>
            <dd>{requestedTerms?.planName ?? '—'}</dd>
            <dt>Repayment frequency</dt>
            <dd>{data.repaymentFrequency ?? requestedTerms?.repaymentFrequency ?? '—'}</dd>
            {data.repaymentScheduleModel === 'ADMIN_APPROVED_DATE_V1' ? (
              <>
                <dt>Admin-approved first repayment date</dt>
                <dd>{formatBusinessDate(data.approvedFirstRepaymentDate)}</dd>
              </>
            ) : (
              <>
                <dt>Historical debit day</dt>
                <dd>{data.preferredDebitDay === null ? '—' : `Day ${data.preferredDebitDay}`}</dd>
                <dt>Expected first due date</dt>
                <dd>{formatBusinessDate(data.plannedFirstDueDate)}</dd>
              </>
            )}
            <dt>Installation / delivery address</dt>
            <dd>
              {requestedTerms?.installationAddressSnapshot === null ||
              requestedTerms?.installationAddressSnapshot === undefined
                ? '—'
                : `${requestedTerms.installationAddressSnapshot.addressLine1}, ${requestedTerms.installationAddressSnapshot.city}, ${requestedTerms.installationAddressSnapshot.stateCode}`}
            </dd>
          </dl>

          {data.approvedRepaymentSchedule != null && (
            <ApprovedRepaymentSchedule rows={data.approvedRepaymentSchedule} />
          )}

          {data.status === 'AWAITING_CUSTOMER_ACCEPTANCE' && data.offerPartyPreview !== null && (
            <div>
              <h3>Contracting customer</h3>
              <dl className={styles.definitionList}>
                <dt>Legal name</dt>
                <dd>{data.offerPartyPreview.legalName}</dd>
                <dt>Verified email</dt>
                <dd>{data.offerPartyPreview.verifiedEmail}</dd>
                <dt>Phone</dt>
                <dd>{data.offerPartyPreview.phoneNumber}</dd>
                <dt>Residential address</dt>
                <dd>
                  {data.offerPartyPreview.residentialAddress.addressLine1},{' '}
                  {data.offerPartyPreview.residentialAddress.city},{' '}
                  {data.offerPartyPreview.residentialAddress.stateCode}
                </dd>
              </dl>
            </div>
          )}

          {canAccept && (
            <Alert tone="info">
              Acceptance preserves this identity, location, commercial offer, existing platform
              acknowledgements and the exact accepted document hash. Direct Debit authorization is
              completed separately after acceptance.
            </Alert>
          )}

          {canAccept && (
            <div>
              <h3>Exact contract document</h3>
              {contractPreview.isPending && <PageSpinner label="Preparing contract preview" />}
              {contractPreview.isError && (
                <Alert tone="error">{toDisplayMessage(contractPreview.error)}</Alert>
              )}
              {contractPreview.isSuccess && (
                <>
                  <p>
                    Document {contractPreview.data.templateVersion} · SHA-256{' '}
                    <span className={styles.hashValue}>{contractPreview.data.contentSha256}</span>
                  </p>
                  <pre className={styles.contractDocument}>
                    {contractPreview.data.canonicalContent}
                  </pre>
                </>
              )}
            </div>
          )}
        </section>
      )}

      {data.offerExpired === true && (
        <Alert tone="warning">
          This unaccepted offer has expired. Staff must return the application to review and approve
          a new first repayment date. You will review and accept the revised offer separately; no
          date is shifted automatically.
        </Alert>
      )}
      {data.changeRequestReason !== null && (
        <Alert tone="info">Changes requested: {data.changeRequestReason}</Alert>
      )}
      {data.rejectionReason !== null && (
        <Alert tone="error">Rejected: {data.rejectionReason}</Alert>
      )}

      <div className={styles.actions}>
        {canSubmit && (
          <>
            <Link to={routeTo.customerApplicationEdit(id)}>
              <Button variant="secondary">
                {data.status === 'CHANGES_REQUESTED'
                  ? 'Make requested changes'
                  : 'Continue application'}
              </Button>
            </Link>
            <Button disabled={submit.isPending} onClick={() => submit.mutate()}>
              Submit for review
            </Button>
          </>
        )}
        {canAccept && (
          <div className={styles.acceptancePanel}>
            <h2>Sign and accept</h2>
            <p>
              Upload a clear PNG or JPEG image of your signature. Maximum size 2 MB. You can replace
              it until you complete final acceptance.
            </p>
            <label className={styles.fileField}>
              <span>Signature image</span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(event) => {
                  const selected = event.target.files?.[0] ?? null;
                  setSignatureDocumentId(null);
                  setSignatureFile(selected);
                  if (selected === null) setSignatureError(null);
                  else if (!['image/png', 'image/jpeg'].includes(selected.type)) {
                    setSignatureError('Choose a PNG or JPEG signature image. SVG is not accepted.');
                  } else if (selected.size > 2_000_000) {
                    setSignatureError('The signature image must be 2 MB or smaller.');
                  } else {
                    setSignatureError(null);
                  }
                }}
              />
            </label>
            {signatureError !== null && <Alert tone="error">{signatureError}</Alert>}
            {signaturePreviewUrl !== null && (
              <div className={styles.signaturePreview}>
                <img src={signaturePreviewUrl} alt="Signature preview" />
              </div>
            )}
            <Button
              type="button"
              variant="secondary"
              disabled={
                signatureFile === null || signatureError !== null || uploadSignature.isPending
              }
              onClick={() => uploadSignature.mutate()}
            >
              {uploadSignature.isPending
                ? 'Uploading signature…'
                : signatureDocumentId === null
                  ? 'Upload signature'
                  : 'Replace uploaded signature'}
            </Button>
            {signatureDocumentId !== null && (
              <Alert tone="success">Signature uploaded privately and ready for acceptance.</Alert>
            )}
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={confirmedOfferVersion === data.version}
                onChange={(event) =>
                  setConfirmedOfferVersion(event.target.checked ? data.version : null)
                }
              />
              I have read and accept the exact contract document, final offer and platform
              acknowledgements shown above, and I confirm the uploaded signature is mine.
            </label>
            <Button
              disabled={
                accept.isPending ||
                confirmedOfferVersion !== data.version ||
                signatureDocumentId === null ||
                !contractPreview.isSuccess ||
                contractPreview.data?.offerVersion !== data.version
              }
              onClick={() => accept.mutate()}
            >
              Accept final offer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function RequestedTerms({
  terms,
}: {
  readonly terms: RequestedCommercialTerms;
}): React.JSX.Element {
  return (
    <section className={styles.card} aria-labelledby="requested-terms-heading">
      <h2 className={styles.cardTitle} id="requested-terms-heading">
        Requested terms
      </h2>
      <p>Captured when this application was submitted. Final terms are confirmed after review.</p>
      <dl className={styles.definitionList}>
        <dt>Product</dt>
        <dd>{terms.productName}</dd>
        <dt>Repayment plan</dt>
        <dd>{terms.planName}</dd>
        <dt>Product price</dt>
        <dd>{formatNgn(terms.productBasePrice)}</dd>
        <dt>Deposit</dt>
        <dd>{formatNgn(terms.depositAmount)}</dd>
        <dt>Financed amount</dt>
        <dd>{formatNgn(terms.financedAmount)}</dd>
        <dt>Regular instalment</dt>
        <dd>{formatNgn(terms.instalmentAmount)}</dd>
        <dt>Final instalment</dt>
        <dd>{formatNgn(terms.finalInstalmentAmount)}</dd>
        <dt>Total repayable</dt>
        <dd>{formatNgn(terms.totalRepayableAmount)}</dd>
        <dt>Tenure</dt>
        <dd>{terms.tenureMonths} months</dd>
        {terms.preferredDebitDay === null && terms.requestedFirstDueDate === null ? (
          <>
            <dt>Repayment schedule</dt>
            <dd>Established during Admin review; shown before contract acceptance.</dd>
          </>
        ) : (
          <>
            <dt>Historical preferred debit day</dt>
            <dd>
              {terms.preferredDebitDay === null ? 'Not recorded' : `Day ${terms.preferredDebitDay}`}
            </dd>
            <dt>Historical requested first due date</dt>
            <dd>{formatBusinessDate(terms.requestedFirstDueDate)}</dd>
          </>
        )}
        <dt>Submitted quote captured</dt>
        <dd>{new Date(terms.capturedAt).toLocaleString()}</dd>
      </dl>
    </section>
  );
}

function PaymentPreferences({
  adminDateFlow,
  debitDay,
  firstDueDate,
}: {
  readonly adminDateFlow: boolean;
  readonly debitDay: number | null;
  readonly firstDueDate: string | null;
}): React.JSX.Element {
  if (adminDateFlow)
    return (
      <p>
        Admin establishes the first repayment date and schedule during review. You will see both
        before contract acceptance.
      </p>
    );
  return (
    <dl className={styles.definitionList}>
      <dt>Preferred debit day</dt>
      <dd>{debitDay === null ? 'Not recorded' : `Day ${debitDay}`}</dd>
      <dt>Requested first due date</dt>
      <dd>{firstDueDate === null ? 'Not recorded' : formatBusinessDate(firstDueDate)}</dd>
    </dl>
  );
}
